/* mpf-refine: sort and narrow a rotary-instrument grid in the page (owner, 2026-10-19: "more sorting options").
   All products are on one page (sections set 100 per page when snippets/mpf-dense applies), each card's <li> carries
   data-mpf-title / -sku / -price / -available. Type, grit and bur shape are read from the title, as on mpfbrush.com.
   State lives in the URL hash (#type=…&grit=…&shape=…&sort=…&stock=1) so a refined list can be shared or reloaded. */
(() => {
  if (customElements.get('mpf-refine')) return;

  const TYPES = [
    ['Diamond burs', t => /diamond burs/i.test(t)],
    ['Carbide burs', t => /carbide/i.test(t)],
    ['Rubber wheels & points', t => /rubber wheels/i.test(t)],
    ['Diamond discs', t => /diamond (cutter )?disc/i.test(t)],
    ['Polishing brushes & mops', t => /polishing (brushes|mops)/i.test(t)],
    ['Mandrels & adapters', t => /mandrel|converter/i.test(t)],
    ['Grinding & polishing', () => true],
  ];
  const GRITS = [ // most specific first; the order of this list is also the fine → coarse order
    ['Super fine', /super\s*fine/i], ['Medium to fine', /medium\s*to\s*fine/i], ['Fine', /\bfine\b/i],
    ['Medium to coarse', /medium\s*to\s*coarse/i], ['Super coarse', /super\s*coarse/i], ['Medium', /\bmedium\b/i], ['Coarse', /\bcoarse\b/i],
  ];
  const GRIT_ORDER = ['Super fine', 'Fine', 'Medium to fine', 'Medium', 'Medium to coarse', 'Coarse', 'Super coarse'];
  const SORTS = [
    ['', 'Featured'], ['name', 'Name, A–Z'], ['name-desc', 'Name, Z–A'], ['price', 'Price, low to high'],
    ['price-desc', 'Price, high to low'], ['sku', 'Item number'], ['grit', 'Grit, fine to coarse'], ['grit-desc', 'Grit, coarse to fine'],
  ];
  const esc = s => String(s).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

  class MpfRefine extends HTMLElement {
    connectedCallback() {
      this.grid = this.parentElement.querySelector('ul.product-grid');
      if (!this.grid) return;
      // data-mode="groups" (lab tools): Type is the product's mpfbrush.com group from #mpf-groups, no grit or shape
      const grouped = this.dataset.mode === 'groups';
      let groups = {};
      if (grouped) try { groups = JSON.parse(document.getElementById('mpf-groups').textContent); } catch (e) { }
      this.typeOrder = grouped ? [...new Set(Object.values(groups).map(g => g[1]))].concat('Other') : TYPES.map(t => t[0]);
      this.sorts = grouped ? SORTS.filter(([v]) => !v.startsWith('grit')) : SORTS;
      this.items = [...this.grid.children].filter(li => li.dataset.mpfTitle).map((li, i) => {
        const t = li.dataset.mpfTitle;
        const grit = grouped ? '' : (GRITS.find(([, re]) => re.test(t)) || [''])[0];
        const shape = grouped ? '' : (t.match(/ - ([A-Z]{2})-\d/) || [])[1] || '';
        const type = grouped ? ((groups[li.dataset.mpfHandle] || [])[1] || 'Other') : TYPES.find(([, f]) => f(t))[0];
        return { li, i, t, grit, shape, type, sku: li.dataset.mpfSku || '', price: +li.dataset.mpfPrice || 0, avail: li.dataset.mpfAvailable === 'true' };
      });
      if (this.items.length < 2) return;
      this.state = Object.fromEntries(new URLSearchParams(location.hash.slice(1)));
      this.render();
      this.apply();
      window.addEventListener('hashchange', () => { this.state = Object.fromEntries(new URLSearchParams(location.hash.slice(1))); this.render(); this.apply(); });
    }
    // values of one facet among the items that pass every other facet, so counts always add up
    values(key) {
      const pass = this.items.filter(it => this.match(it, key));
      const n = {}; pass.forEach(it => { if (it[key]) n[it[key]] = (n[it[key]] || 0) + 1; });
      const order = key === 'grit' ? GRIT_ORDER : key === 'type' ? this.typeOrder : Object.keys(n).sort();
      return order.filter(v => n[v]).map(v => [v, n[v]]);
    }
    match(it, skip) {
      for (const k of ['type', 'grit', 'shape']) if (k !== skip && this.state[k] && it[k] !== this.state[k]) return false;
      if (this.state.stock && !it.avail) return false;
      return true;
    }
    group(key, label) {
      const vals = this.values(key);
      if (vals.length < 2 && !this.state[key]) return '';
      const all = vals.reduce((a, [, n]) => a + n, 0);
      const chip = (v, txt, n) => `<button type="button" class="mpf-refine__chip" data-k="${key}" data-v="${esc(v)}" aria-pressed="${(this.state[key] || '') === v}">${esc(txt)}<span>${n}</span></button>`;
      return `<div class="mpf-refine__row" role="group" aria-label="${label}"><span class="mpf-refine__label">${label}</span>${chip('', 'All', all)}${vals.map(([v, n]) => chip(v, v, n)).join('')}</div>`;
    }
    render() {
      const shown = this.items.filter(it => this.match(it)).length;
      this.innerHTML = `
        <div class="mpf-refine__rows">${this.group('type', 'Type')}${this.group('grit', 'Grit')}${this.shapes() ? this.group('shape', 'Shape') : ''}</div>
        <div class="mpf-refine__bar">
          <p class="mpf-refine__count" role="status">${shown === this.items.length ? `${shown} products` : `${shown} of ${this.items.length} products`}</p>
          <label class="mpf-refine__stock"><input type="checkbox"${this.state.stock ? ' checked' : ''}> In stock only</label>
          <label class="mpf-refine__sort">Sort by <select>${this.sorts.map(([v, t]) => `<option value="${v}"${(this.state.sort || '') === v ? ' selected' : ''}>${t}</option>`).join('')}</select></label>
          ${['type', 'grit', 'shape', 'stock'].some(k => this.state[k]) ? '<button type="button" class="mpf-refine__clear">Clear</button>' : ''}
        </div>`;
      this.querySelectorAll('.mpf-refine__chip').forEach(b => b.addEventListener('click', () => this.set(b.dataset.k, b.dataset.v)));
      this.querySelector('.mpf-refine__stock input').addEventListener('change', e => this.set('stock', e.target.checked ? '1' : ''));
      this.querySelector('.mpf-refine__sort select').addEventListener('change', e => this.set('sort', e.target.value));
      const clr = this.querySelector('.mpf-refine__clear'); if (clr) clr.addEventListener('click', () => { this.state = { sort: this.state.sort || '' }; this.commit(); });
    }
    // bur shape codes (SI, TR, FO…) only exist for diamond burs: the row shows once those are chosen, or on an all-diamond-bur page
    shapes() { return this.state.type === 'Diamond burs' || this.items.every(it => it.type === 'Diamond burs'); }
    set(k, v) {
      this.state[k] = v;
      if (k === 'type' && !this.shapes()) this.state.shape = '';
      this.commit();
    }
    commit() {
      const q = new URLSearchParams(Object.entries(this.state).filter(([, v]) => v)).toString();
      history.replaceState(null, '', q ? '#' + q : location.pathname + location.search);
      this.render(); this.apply();
    }
    apply() {
      const s = this.state.sort || '', d = s.endsWith('-desc') ? -1 : 1, key = s.replace('-desc', '');
      const g = it => (it.grit ? GRIT_ORDER.indexOf(it.grit) : 99);
      const cmp = {
        name: (a, b) => a.t.localeCompare(b.t, undefined, { numeric: true }) * d,
        price: (a, b) => (a.price - b.price) * d || a.i - b.i,
        sku: (a, b) => a.sku.localeCompare(b.sku, undefined, { numeric: true }) || a.i - b.i,
        grit: (a, b) => (g(a) === 99 || g(b) === 99 ? g(a) - g(b) : (g(a) - g(b)) * d) || a.t.localeCompare(b.t, undefined, { numeric: true }),
      }[key] || ((a, b) => a.i - b.i);
      [...this.items].sort(cmp).forEach(it => { it.li.hidden = !this.match(it); this.grid.appendChild(it.li); });
    }
  }
  customElements.define('mpf-refine', MpfRefine);
})();
